import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight } from "@phosphor-icons/react";

const LOGO = "/logo-geekcreative.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) return setErr("Password must be at least 6 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    if (!token) return setErr("Reset link is missing its token. Request a new one.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated — please sign in.");
      setTimeout(() => nav("/login"), 2000);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || "Reset failed. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white text-[var(--gc-navy)] flex flex-col">
      <header className="border-b border-[var(--gc-border)] px-8 h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src={LOGO} alt="GeekCreative" className="h-16 md:h-20 w-auto block" />
        </Link>
        <div className="text-sm text-[var(--gc-text-muted)]">
          <Link to="/login" className="link-underline text-[var(--gc-navy)] font-semibold">Sign in</Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="eyebrow mb-4">Password reset</div>
          <h1 className="font-display text-5xl md:text-6xl">Choose a<br/>new password.</h1>

          {done ? (
            <div className="mt-10 border-l-2 border-[var(--gc-success)] bg-green-50 p-5" data-testid="reset-success">
              <div className="eyebrow text-[var(--gc-success)] mb-2">All set</div>
              <p className="text-sm text-[var(--gc-navy)]">Your password has been updated. Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-8" data-testid="reset-form">
              {err && <div className="border-l-2 border-[var(--gc-danger)] bg-red-50 text-[var(--gc-danger)] p-3 text-sm" data-testid="reset-error">{err}</div>}
              <div>
                <div className="eyebrow mb-2">New password</div>
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  required
                  className="gc-input"
                  data-testid="reset-password"
                  placeholder="At least 6 characters"
                />
              </div>
              <div>
                <div className="eyebrow mb-2">Confirm password</div>
                <input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  type="password"
                  required
                  className="gc-input"
                  data-testid="reset-confirm"
                  placeholder="Type it again"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-solid w-full justify-center disabled:opacity-50"
                data-testid="reset-submit"
              >
                {loading ? "Updating…" : <>Update password <ArrowUpRight size={16} weight="bold" /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
